/** Partnership MOU — the form (Tom 2026-09-10: "the same way you made
 * contractor template … in deals under purchasing at the bottom. the things
 * that should be changable are the llcs, addresses, parcel, ein and names").
 * Opens from the deal's Purchasing tab with the property address and parcel
 * filled; pick a saved partner (or type one), check the two sides, download
 * the filled Word file. */
import { useEffect, useState } from "react";
import {
  MOU_BUILTIN, PROFIT_SPLITS, downloadBlob, firstNames, fmtEin, generateMou, getMouDefaults, matchProfitSplit,
  parsePeople, profitSplitLabel, saveMouDefaults,
  type MouDefaults, type MouPartner, type MouValues,
} from "./api";

export interface MouSeed {
  propertyAddress: string;
  legalDescription?: string | null;
  /** the deal's assigned partner name (Internal Assignment) — matches a saved partner */
  partnerHint?: string | null;
}

export function PartnershipMouModal({ token, isAdmin, seed, onClose }: {
  token: string | null;
  isAdmin: boolean;
  seed: MouSeed;
  onClose: () => void;
}) {
  const [defaults, setDefaults] = useState<MouDefaults>(MOU_BUILTIN);
  const [partners, setPartners] = useState<MouPartner[]>([]);
  const [peopleText, setPeopleText] = useState("");
  const [v, setV] = useState<MouValues>(() => ({
    propertyAddress: seed.propertyAddress,
    legalDescription: seed.legalDescription ?? "",
    partnerLlc: "", partnerEin: "",
    ...MOU_BUILTIN,
  }));
  const [busy, setBusy] = useState<"" | "gen" | "save">("");
  const [note, setNote] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [mineOpen, setMineOpen] = useState(false);
  // "Custom" shows the two % boxes; it opens by itself when the saved split
  // isn't one of the presets, so a stored 70/30 is never hidden.
  const [customSplit, setCustomSplit] = useState(false);

  const applyPartner = (p: MouPartner) => {
    setPeopleText(p.people.join(" & "));
    setV((x) => ({ ...x, partnerPeople: p.people, partnerLlc: p.llc, partnerEin: p.ein }));
  };

  useEffect(() => {
    let alive = true;
    void getMouDefaults(token).then(({ defaults: d, partners: ps }) => {
      if (!alive) return;
      setDefaults(d);
      setPartners(ps);
      setV((x) => ({ ...x, tomName: d.tomName, tomLlc: d.tomLlc, tomEin: d.tomEin, lender: d.lender, tomPct: d.tomPct, partnerPct: d.partnerPct }));
      if (!matchProfitSplit(d.tomPct, d.partnerPct)) setCustomSplit(true);
      // the deal's assigned partner ("Omer & Eric") → the saved partner side
      const hint = (seed.partnerHint ?? "").trim().toLowerCase();
      if (hint) {
        const hit = ps.find((p) => p.label.toLowerCase().includes(hint) || firstNames(p.people).toLowerCase() === hint
          || p.people.some((n) => hint.includes(n.split(/\s+/)[0]!.toLowerCase())));
        if (hit) applyPartner(hit);
      }
    }).catch(() => { /* built-ins stay */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const set = (k: keyof MouValues, val: string) => setV((x) => ({ ...x, [k]: val }));
  const people = parsePeople(peopleText);
  useEffect(() => { setV((x) => ({ ...x, partnerPeople: people })); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [peopleText]);

  const tomPct = Number(v.tomPct || 0);
  const partnerPct = Number(v.partnerPct || 0);
  const missing: string[] = [];
  if (!people.length) missing.push("partner name");
  if (people.length > 2) missing.push("at most two partner people (the agreement has two partner signature lines)");
  if (!v.partnerLlc?.trim()) missing.push("partner LLC");
  if ((v.partnerEin || "").replace(/\D/g, "").length !== 9) missing.push("partner EIN (9 digits)");
  if (!v.propertyAddress?.trim()) missing.push("property address");
  if (!v.tomName?.trim() || !v.tomLlc?.trim()) missing.push("your name/LLC");
  if ((v.tomEin || "").replace(/\D/g, "").length !== 9) missing.push("your EIN (9 digits)");
  if (!(tomPct >= 0 && partnerPct >= 0 && tomPct + partnerPct === 100)) missing.push("profit split adding to 100%");

  const generate = async () => {
    if (busy) return;
    if (missing.length) { setNote({ kind: "err", text: "Still needed: " + missing.join(", ") + "." }); return; }
    setBusy("gen"); setNote(null);
    try {
      const { blob, filename } = await generateMou(token, { ...v, partnerPeople: people, partnerEin: fmtEin(v.partnerEin || ""), tomEin: fmtEin(v.tomEin || "") });
      downloadBlob(blob, filename);
      setNote({ kind: "ok", text: `Downloaded ${filename} — open it in Word or Google Docs to review and send.` });
    } catch (e) {
      setNote({ kind: "err", text: (e as { detail?: string })?.detail ?? (e as Error)?.message ?? "Generate failed" });
    } finally {
      setBusy("");
    }
  };

  const savePartner = async () => {
    if (!people.length || !v.partnerLlc?.trim()) { setNote({ kind: "err", text: "Type the partner name(s) and LLC first." }); return; }
    setBusy("save"); setNote(null);
    try {
      const entry: MouPartner = { label: `${firstNames(people)} — ${v.partnerLlc!.trim()}`, people, llc: v.partnerLlc!.trim(), ein: fmtEin(v.partnerEin || "") };
      const next = [...partners.filter((p) => p.llc.toLowerCase() !== entry.llc.toLowerCase() || firstNames(p.people) !== firstNames(entry.people)), entry];
      await saveMouDefaults(token, { partners: next });
      setPartners(next);
      setNote({ kind: "ok", text: `Saved "${entry.label}" — it's in the partner list from now on.` });
    } catch (e) {
      setNote({ kind: "err", text: (e as { detail?: string })?.detail ?? (e as Error)?.message ?? "Save failed" });
    } finally {
      setBusy("");
    }
  };

  const saveMine = async () => {
    setBusy("save"); setNote(null);
    try {
      const d: MouDefaults = {
        tomName: v.tomName || defaults.tomName, tomLlc: v.tomLlc || defaults.tomLlc, tomEin: fmtEin(v.tomEin || defaults.tomEin),
        lender: v.lender || defaults.lender, tomPct: v.tomPct || defaults.tomPct, partnerPct: v.partnerPct || defaults.partnerPct,
      };
      await saveMouDefaults(token, { defaults: d });
      setDefaults(d);
      setNote({ kind: "ok", text: "Saved as the defaults for every new MOU." });
    } catch (e) {
      setNote({ kind: "err", text: (e as { detail?: string })?.detail ?? (e as Error)?.message ?? "Save failed" });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="modalscrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal fbmodal camodal" role="dialog" aria-modal="true" aria-label="Partnership MOU">
        <div className="modalhd">
          <h2>🤝 Partnership Agreement (MOU)</h2>
          <button className="modalx" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="casec">
          <div className="casechd">
            <span>Partner side</span>
            <span className="casecacts">
              {partners.length ? (
                <select className="moneyinput" value="" onChange={(e) => { const p = partners[Number(e.target.value)]; if (p) applyPartner(p); }} aria-label="Pick a saved partner">
                  <option value="">Saved partners…</option>
                  {partners.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
                </select>
              ) : null}
              {isAdmin ? <button className="btn slim" disabled={busy !== ""} onClick={() => void savePartner()}>Save this partner</button> : null}
            </span>
          </div>
          <div className="fbgrid">
            <label className="wide">Partner name(s) — one or two people, e.g. "Omer Yacobovitch & Eric Rahamim"
              <input value={peopleText} placeholder="Omer Yacobovitch & Eric Rahamim" onChange={(e) => setPeopleText(e.target.value)} />
            </label>
            <label>Partner LLC<input value={v.partnerLlc ?? ""} placeholder="Orange Build & Design LLC" onChange={(e) => set("partnerLlc", e.target.value)} /></label>
            <label>Partner EIN<input inputMode="numeric" value={v.partnerEin ?? ""} placeholder="99-4343241" onChange={(e) => set("partnerEin", e.target.value)} onBlur={(e) => set("partnerEin", fmtEin(e.target.value))} /></label>
          </div>
          {people.length ? <div className="sub" style={{ marginTop: 6 }}>In the agreement: <b>{people.join(" & ")}</b> / {v.partnerLlc || "…"} · referred to as "the <b>{firstNames(people)}</b> Side" · {people.length} signature line{people.length === 1 ? "" : "s"}</div> : null}
        </div>

        <div className="casec">
          <div className="casechd"><span>Property</span></div>
          <div className="fbgrid">
            <label className="wide">Property address<input value={v.propertyAddress ?? ""} onChange={(e) => set("propertyAddress", e.target.value)} /></label>
            <label className="wide">Legal description / parcel ID<input value={v.legalDescription ?? ""} placeholder="e.g. Parcel 12-34-56-7890 or the legal description" onChange={(e) => set("legalDescription", e.target.value)} /></label>
          </div>
        </div>

        <div className="casec">
          <div className="casechd"><span>Profit split &amp; lender</span></div>
          <div className="casplit">
            <div className="fld-l">Profit split</div>
            <div className="casplitpre" role="group" aria-label="Profit split">
              {PROFIT_SPLITS.map((s) => {
                const on = !customSplit && tomPct === s.tom && partnerPct === s.partner;
                return (
                  <button key={`${s.tom}-${s.partner}`} type="button" className={"btn slim" + (on ? " primary" : "")} aria-pressed={on}
                    onClick={() => { setCustomSplit(false); setV((x) => ({ ...x, tomPct: String(s.tom), partnerPct: String(s.partner) })); }}>
                    {profitSplitLabel(s, people.length ? firstNames(people) : undefined)}
                  </button>
                );
              })}
              <button type="button" className={"btn slim" + (customSplit ? " primary" : "")} aria-pressed={customSplit} onClick={() => setCustomSplit(true)}>Custom…</button>
            </div>
            {customSplit ? (
              <div className="fbgrid" style={{ marginTop: 8 }}>
                <label>Your share (%)<input inputMode="numeric" value={v.tomPct ?? ""} onChange={(e) => { const t = e.target.value; setV((x) => ({ ...x, tomPct: t, partnerPct: /^\d+$/.test(t) && Number(t) <= 100 ? String(100 - Number(t)) : (x.partnerPct ?? "") })); }} /></label>
                <label>Partner share (%)<input inputMode="numeric" value={v.partnerPct ?? ""} onChange={(e) => set("partnerPct", e.target.value)} /></label>
              </div>
            ) : null}
            {tomPct + partnerPct === 100 ? (
              <div className="capreview"><b>{tomPct}%</b> to {v.tomName || "you"} / {v.tomLlc || "…"} · <b>{partnerPct}%</b> to {people.length ? people.join(" & ") : "partner"} / {v.partnerLlc || "…"}</div>
            ) : null}
          </div>
          <div className="fbgrid" style={{ marginTop: 8 }}>
            <label>Lender<input value={v.lender ?? ""} onChange={(e) => set("lender", e.target.value)} /></label>
          </div>
        </div>

        <div className="casec">
          <button type="button" className="casechd castoggle" onClick={() => setMineOpen((o) => !o)}>
            <span>Your side</span><span className="sub">{v.tomName} / {v.tomLlc} · EIN {v.tomEin} {mineOpen ? "▴" : "▾"}</span>
          </button>
          {mineOpen ? (
            <div className="fbgrid">
              <label>Your name<input value={v.tomName ?? ""} onChange={(e) => set("tomName", e.target.value)} /></label>
              <label>Your LLC<input value={v.tomLlc ?? ""} onChange={(e) => set("tomLlc", e.target.value)} /></label>
              <label>Your EIN<input inputMode="numeric" value={v.tomEin ?? ""} onChange={(e) => set("tomEin", e.target.value)} onBlur={(e) => set("tomEin", fmtEin(e.target.value))} /></label>
            </div>
          ) : null}
        </div>

        <div className="modalacts">
          <button className={"btn primary" + (missing.length ? " soft" : "")} disabled={busy !== ""} onClick={() => void generate()}>
            {busy === "gen" ? "Building the Word file…" : "Generate & download .docx"}
          </button>
          <button className="btn" onClick={onClose}>Close</button>
          {isAdmin ? <button className="btn ghost" disabled={busy !== ""} onClick={() => void saveMine()}>Save my side/split/lender as defaults</button> : null}
        </div>
        {missing.length && !note ? <div className="sub" style={{ marginTop: 8 }}>Still needed: {missing.join(", ")}.</div> : null}
        {note ? <div className={note.kind === "err" ? "caerr" : "fbnote"} role={note.kind === "err" ? "alert" : undefined}>{note.text}</div> : null}
      </div>
    </div>
  );
}
